// Action
import { isTrackableAction } from "../actions/ITrackableAction";
// Errors
import { NotSupportedError } from "@ms/uno-errors/lib/local/errors/NotSupportedError";
import { ShimStore, ShimStoreId } from "../stores/ShimStore";
// Utilities
import remove from "lodash/remove";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
export class AsyncDispatcher {
    dispatchActionAsync(action) {
        return this.batchDispatchActionsAsync(action);
    }
    async batchDispatchActionsAsync(...actions) {
        if (this.enableAsyncDispatchV2) {
            return this.batchDispatchActionsInternalAsync(()=>true, ...actions);
        } else {
            return this.legacyBatchDispatchActionsInternalAsync(()=>true, ...actions);
        }
    }
    conditionalDispatchActionAsync(precondition, action) {
        return this.legacyBatchDispatchActionsInternalAsync(precondition, action);
    }
    /**
     * Dispatches multiple actions and publishes one store update for all of them
     * @param precondition A precondition that is evaluated right before the action is dispatched synchronously to any store
     * @param actions The actions to dispatch
     */ async batchDispatchActionsInternalAsync(precondition, ...actions) {
        let anyActionDispatched = false;
        await Promise.all(actions.map(async (action)=>{
            if (action.isLoggingEnabled()) {
                this.logAction({
                    name: action.type,
                    exportName: action.getExportName(),
                    payload: action.loggingData()
                });
            }
            const storeTypes = this.registry.getStoreTypesForAction(action.type);
            await Promise.all(storeTypes.map(async (storeType)=>{
                try {
                    const store = await this.storeProvider(storeType)();
                    // If there are pending subscriptions for this store, register them now
                    this.applyPendingSubscriptions(storeType);
                    if (precondition()) {
                        this.logTrace(0x1e2e0660 /* tag_4l6z6 */ , TraceLevel.Verbose, `Dispatching action [ActionType=${action.type}][Store=${store.name}]`);
                        store.handleAction(action);
                        anyActionDispatched = true;
                    }
                } catch (e) {
                    this.logTrace(0x1e2e0661 /* tag_4l6z7 */ , TraceLevel.Error, `Error while dispatching action [ActionType=${action.type}][Store=${storeType}][Error=${ErrorUtilities.getMessage(e)}]`);
                }
            }));
        }));
        // Notify all subscribers if any action was dispatched
        if (anyActionDispatched) {
            for (const callback of this.onActionDispatchCompleteSubscribers.slice()){
                callback();
            }
        }
    }
    /**
     * Dispatches multiple actions and publishes one store update for all of them
     * @param precondition A precondition that is evaluated right before the action is dispatched synchronously to any store
     * @param actions The actions to dispatch
     */ async legacyBatchDispatchActionsInternalAsync(precondition, ...actions) {
        const renderMeasurementActionInfo = [];
        const trackableActions = [];
        let anyActionDispatched = false;
        for (const action of actions){
            if (action.isLoggingEnabled()) {
                this.logAction({
                    name: action.type,
                    exportName: action.getExportName(),
                    payload: action.loggingData()
                });
            }
            if (isTrackableAction(action)) {
                renderMeasurementActionInfo.push({
                    actionId: action.actionId,
                    actionName: action.type
                });
                trackableActions.push(action);
            }
            const storeTypes = this.registry.getStoreTypesForAction(action.type);
            const storePromises = storeTypes.map(async (storeType)=>{
                try {
                    const store = await this.storeProvider(storeType)();
                    // If there are pending subscriptions for this store, register them now
                    this.applyPendingSubscriptions(storeType);
                    return store;
                } catch (e) {
                    if (e instanceof NotSupportedError) {
                        this.logTrace(0x1e2e0662 /* tag_4l6z8 */ , TraceLevel.Verbose, `Unsupported store found. Ignoring. [ActionType=${action.type}][StoreType=${storeType}]`);
                    } else {
                        throw e;
                    }
                }
            });
            const stores = await Promise.all(storePromises).catch((e)=>{
                this.logTrace(0x1e25a854 /* tag_4j07u */ , TraceLevel.Error, `Error while dispatching action [ActionType=${action.type}][Error=${ErrorUtilities.getMessage(e)}]`);
                throw e;
            });
            if (precondition()) {
                for (const store of stores){
                    if (store) {
                        try {
                            this.logTrace(0x1e25a855 /* tag_4j07v */ , TraceLevel.Verbose, `Dispatching action [ActionType=${action.type}][Store=${store.name}]`);
                            store.handleAction(action);
                            anyActionDispatched = true;
                        } catch (e) {
                            this.logTrace(0x1e2e065f /* tag_4l6z5 */ , TraceLevel.Warning, `Error while dispatching action [ActionType=${action.type}][Store=${store.name}][Error=${ErrorUtilities.getMessage(e)}]`);
                        }
                    }
                }
            }
        }
        // Notify all subscribers if any action was dispatched
        if (anyActionDispatched) {
            // Clone the subscribers in case a callback removes a subscriber mid-iteration
            const cloneSubscribers = this.onActionDispatchCompleteSubscribers.slice();
            for (const callback of cloneSubscribers){
                callback(renderMeasurementActionInfo);
            }
            const cloneSubscribersForTrackableActions = this.onTrackableActiOnDispatchCompleteSubscribers.slice();
            for (const callback of cloneSubscribersForTrackableActions){
                callback(trackableActions);
            }
        }
    }
    addOnActionDispatchComplete(callback) {
        this.onActionDispatchCompleteSubscribers.push(callback);
    }
    removeOnActionDispatchComplete(callback) {
        remove(this.onActionDispatchCompleteSubscribers, (cb)=>{
            return cb === callback;
        });
    }
    subscribeOnActionDispatchComplete(callback) {
        this.onTrackableActiOnDispatchCompleteSubscribers.push(callback);
    }
    registerSubscription(stores, callback) {
        for (const store of stores){
            const storeReference = this.storeReferenceProvider(store)();
            if (this.isStoreAvailableForSubscription(storeReference)) {
                // If the actual store is available, subscribe to it directly
                storeReference.subscribeToStore(callback);
            } else {
                // Otherwise, mark it as pending so that we can subscribe when the store is made available
                if (!this.pendingStoreSubscriptions[store]) {
                    this.pendingStoreSubscriptions[store] = [];
                }
                // Add callback to the pending subscriptions if not already added
                if (!this.pendingStoreSubscriptions[store].includes(callback)) {
                    this.pendingStoreSubscriptions[store].push(callback);
                }
            }
        }
    }
    unregisterSubscription(stores, callback) {
        for (const store of stores){
            /**
             * Cleanup the subscriptions from the store if reference is non-null.
             * This might be shim store if the un-registration happens even before
             * the store got downloaded, in which case, this is a no-op (because shim
             * stores do not have any subscriptions).
             */ const storeReference = this.storeReferenceProvider(store)();
            if (storeReference) {
                storeReference.unsubscribeFromStore(callback);
            }
            // Cleanup the pending subscriptions
            if (this.pendingStoreSubscriptions[store]) {
                remove(this.pendingStoreSubscriptions[store], (cb)=>cb === callback);
            }
        }
    }
    /**
     * Applies the pending subscriptions for the given store type
     * @param storeType The store type
     */ applyPendingSubscriptions(storeType) {
        const storeReference = this.storeReferenceProvider(storeType)();
        if (storeReference && this.pendingStoreSubscriptions[storeType]) {
            this.pendingStoreSubscriptions[storeType].forEach((subscriber)=>{
                storeReference.subscribeToStore(subscriber);
            });
            delete this.pendingStoreSubscriptions[storeType];
        }
    }
    /**
     * Checks whether the full reactive store is available for dispatch
     * @param storeReference The store reference
     */ isStoreAvailableForSubscription(storeReference) {
        const isShimStore = storeReference instanceof ShimStore && storeReference.name === ShimStoreId;
        return storeReference !== null && !isShimStore;
    }
    /**
     * Creates an instance of the AsyncDispatcher
     * @param registry The registry to use for store lookups
     * @param storeProvider The store provider to use for store lookups (download the store if not available)
     * @param storeReferenceProvider The store reference provider to use for store lookups (get the store reference if loaded, or a null / Shim)
     * @param logAction The action logging function
     * @param logTrace The trace logging function
     * @param enableAsyncDispatchV2 Flag to enable the new async dispatch implementation
     */ constructor(registry, storeProvider, storeReferenceProvider, logAction, logTrace, enableAsyncDispatchV2){
        this.registry = registry;
        this.storeProvider = storeProvider;
        this.storeReferenceProvider = storeReferenceProvider;
        this.logAction = logAction;
        this.logTrace = logTrace;
        this.enableAsyncDispatchV2 = enableAsyncDispatchV2;
        this.onActionDispatchCompleteSubscribers = [];
        this.onTrackableActiOnDispatchCompleteSubscribers = [];
        this.pendingStoreSubscriptions = {};
    }
}
